// Division detail page — ONE component for both chambers.
//
// Mirrors the information architecture of the APH "Division details" page
// (aph.gov.au/.../HoR/Divisions/Details?id=NNNN): title + tally band,
// label/value facts, action buttons, a "Votes by party" stacked column chart,
// then "Individual votes" tabbed by Ayes / Noes / Pair with a By party |
// By member toggle. The rendering is Reading Room, not APH — teal accent via
// theme.palette.ledger.*, flat bordered Paper, hairlines, no uppercase labels
// and no mono.
//
// Two data eras feed this page:
//   * spine  — the ParlInfo/Hansard division record. Always present. Counts,
//              question, outcome and voter NAME STRINGS only (no PHIDs).
//   * aph    — the aph.gov.au Divisions-API twin attached by the API as
//              `data.aph` when the spine carries `aph_division_id`. Per-member
//              votes with PHIDs, party colours, bills, the real subject title.
//              House from 2016-08-31, Senate from 2019-07-02.
// Everything below degrades deliberately to the spine when `aph` is absent:
// the tally, question, outcome and grouped name lists still render, and the
// page says where the data came from instead of showing empty sections.

import React, { useState, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button, Divider, Alert,
  Link, Grid, Avatar, Tabs, Tab, ToggleButton, ToggleButtonGroup, useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, LabelList,
} from 'recharts';
import { getDivision, getSenateDivision } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import { chamberColor } from './explore/charts';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

// ---------------------------------------------------------------- helpers

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Spine voter lists are USUALLY arrays, but some documents carry the list as a
// stringified Python repr ("['Adams, D.G.H.', 'Albanese, A.N.']") — observed on
// 2006 House and 2026 Senate spine docs. Accept both rather than rendering a
// wall of brackets and quotes at the reader. Python repr quotes each element
// with ' unless it contains one, in which case it uses ", so match both forms.
const REPR_ITEM_RE = /'((?:[^'\\]|\\.)*)'|"((?:[^"\\]|\\.)*)"/g;

function asNameList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value !== 'string') return [];
  const s = value.trim();
  if (!s || s === '[]') return [];
  if (!s.startsWith('[')) return [s];
  const out = [];
  let m = REPR_ITEM_RE.exec(s);
  while (m !== null) {
    const raw = m[1] !== undefined ? m[1] : m[2];
    out.push(raw.replace(/\\(['"\\])/g, '$1'));
    m = REPR_ITEM_RE.exec(s);
  }
  REPR_ITEM_RE.lastIndex = 0;
  return out;
}

// Only ever link out to the APH family (TheyVoteForYou was retired from this
// project — never label or link it). Same check as DivisionsSearch's
// parlInfoUrl helper.
function aphOnlyUrl(url) {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return (host === 'aph.gov.au' || host.endsWith('.aph.gov.au')) ? url : null;
  } catch (e) {
    return null;
  }
}

// Division titles arrive as "2026-05-14 Division 1: Second Reading"; the date
// and number are shown separately, so strip that prefix for the headline.
const TITLE_PREFIX_RE = /^\s*\d{4}-\d{2}-\d{2}\s+Division\s+\d+\s*:\s*/i;

// APH reports a vote as "aye" / "no" / "did not vote" (spaces, not
// underscores — the latter appears in some fixtures). Normalise both.
function normaliseVote(v) {
  return String(v || '').toLowerCase().replace(/[\s_]+/g, '_');
}

// APH renders a member as "Mr Buchholz" / "Ms L. M. Chesters" / "Mr L. S.
// O'Brien", so a naive sort on the raw string groups every Dr before every Mr.
// Strip the honorific and any given-name initials to get a surname-ish sort key
// (Senate names like "Nampijinpa Price" carry neither and pass through intact).
const HONORIFIC_RE = /^(?:the\s+)?(?:hon\.?|right\s+hon\.?|mr|mrs|ms|miss|dr|prof\.?|senator)\s+/i;
const INITIAL_RE = /^[A-Z]\.?\s+/;

function surnameKey(name) {
  let s = String(name || '').trim();
  let prev;
  do {
    prev = s;
    s = s.replace(HONORIFIC_RE, '');
  } while (s !== prev);
  do {
    prev = s;
    s = s.replace(INITIAL_RE, '');
  } while (s !== prev);
  return (s || String(name || '')).toLowerCase();
}

function initialsFor(name) {
  const cleaned = String(name || '').replace(/\(.*?\)/g, '').trim();
  const parts = cleaned.split(/[\s,]+/).filter(Boolean);
  const last = parts[parts.length - 1] || '';
  return (last[0] || '?').toUpperCase();
}

/**
 * Flatten the spine document (+ optional APH twin) into one shape the page
 * renders from, so every section below has a single place to look.
 */
function normalise(raw, chamber) {
  const aph = raw.aph || null;
  const isSenate = chamber === 'Senate';

  const spineAye = asNameList(isSenate ? raw.aye_voters : raw.votes_aye);
  const spineNo = asNameList(isSenate ? raw.no_voters : raw.votes_no);
  const spineAbsent = asNameList(isSenate ? raw.absent_voters : raw.votes_absent);

  const strippedTitle = String(raw.title || '').replace(TITLE_PREFIX_RE, '').trim();
  // APH's own title is the bill / subject — what its page shows large. The
  // spine title is a procedural label, used only as the fallback.
  const subject = (aph && aph.title) || strippedTitle || raw.question || 'Division';

  const ayes = Number(aph ? aph.ayes : raw.aye_count) || 0;
  const noes = Number(aph ? aph.noes : raw.no_count) || 0;
  const didNotVote = Number(
    aph ? aph.did_not_vote : (raw.votes_absent_count ?? spineAbsent.length)
  ) || 0;

  const passed = aph && aph.passed !== undefined ? aph.passed : raw.passed;
  const outcome = (aph && aph.result_label)
    ? aph.result_label.charAt(0).toUpperCase() + aph.result_label.slice(1)
    : (passed ? 'Agreed to' : 'Negatived');

  const votes = (aph && Array.isArray(aph.votes) && aph.votes.length)
    ? aph.votes.map((v) => ({ ...v, vote: normaliseVote(v.vote) }))
    : null;

  const partyBreakdown = (raw.party_breakdown && raw.party_breakdown.length)
    ? raw.party_breakdown
    : ((aph && aph.party_breakdown) || []);

  return {
    id: raw.id,
    chamber,
    enriched: Boolean(aph),
    subject,
    question: raw.question || (aph && aph.question) || null,
    context: raw.context || (aph && aph.context) || null,
    date: raw.date,
    divisionNumber: raw.division_number,
    aphDivisionNumber: aph ? aph.division_number : null,
    mover: raw.mover || (aph && aph.mover) || null,
    parliament: aph ? aph.parliament_number : null,
    session: aph ? aph.session_number : null,
    hansardLine: raw.content || null,
    conscienceVote: raw.conscience_vote ?? (aph && aph.conscience_vote),
    castingVote: raw.casting_vote ?? (aph && aph.casting_vote),
    castingVoteReason: (aph && aph.casting_vote_reason) || null,
    majorityRequired: raw.majority_required ?? (aph && aph.majority_required),
    ayes,
    noes,
    didNotVote,
    passed,
    outcome,
    partyBreakdown,
    votes,
    // `bills_linked` is the API's resolution of each bill_id to a real
    // bills-index document id (see _resolve_aph_division_bills); fall back to
    // the raw APH list, which renders unlinked.
    bills: (aph && Array.isArray(aph.bills_linked) && aph.bills_linked.length)
      ? aph.bills_linked
      : ((aph && Array.isArray(aph.bills)) ? aph.bills : []),
    votesUrl: aphOnlyUrl(raw.aph_votes_url || (aph && aph.votes_url)),
    sourceUrl: aphOnlyUrl(raw.source_url),
    spine: { aye: spineAye, no: spineNo, absent: spineAbsent },
  };
}

// --------------------------------------------------------------- sections

/** Label/value facts, the list under APH's headline. */
function FactRow({ label, children }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  if (children === null || children === undefined || children === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.75, borderBottom: `${t.ruleWidth} solid ${t.ruleColor}` }}>
      <Typography
        variant="body2"
        sx={{ color: t.muted, width: { xs: 120, sm: 160 }, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: t.ink }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * The tally box — APH's bordered top-right panel: two big numbers, green over
 * "Ayes" and red over "Noes". Stacks above the facts on a phone so it can
 * never overflow.
 */
function TallyBox({ ayes, noes, didNotVote }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  const figure = (value, label, color) => (
    <Box sx={{ textAlign: 'center', px: { xs: 1.5, sm: 2.5 }, minWidth: 72 }}>
      <Typography
        component="div"
        sx={{
          fontSize: { xs: '2rem', sm: '2.5rem' },
          fontWeight: 600,
          lineHeight: 1.1,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Number(value || 0).toLocaleString()}
      </Typography>
      <Typography variant="body2" sx={{ color: t.muted }}>{label}</Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        border: `${t.ruleWidth} solid ${t.ruleColor}`,
        borderRadius: 1.5,
        py: 1.5,
        px: { xs: 1, sm: 1.5 },
        minWidth: { sm: 220 },
        alignSelf: 'flex-start',
      }}
      role="group"
      aria-label={`Ayes ${ayes}, noes ${noes}${didNotVote ? `, did not vote ${didNotVote}` : ''}`}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap' }}>
        {figure(ayes, 'Ayes', theme.palette.success.main)}
        <Divider orientation="vertical" flexItem />
        {figure(noes, 'Noes', theme.palette.error.main)}
      </Box>
      {didNotVote > 0 && (
        <Typography
          variant="body2"
          sx={{ color: t.faint, textAlign: 'center', mt: 1, fontVariantNumeric: 'tabular-nums' }}
        >
          {didNotVote.toLocaleString()} did not vote
        </Typography>
      )}
    </Box>
  );
}

/**
 * "Votes by party" — two stacked columns (Ayes, Noes), each segment a party in
 * that party's own colour, with the column total printed above. Party colours
 * are DATA marks shipped in `party_breakdown`, never theme colours; the axes
 * and tooltip read the MUI theme so the chart stays legible in dark mode.
 */
function VotesByPartyChart({ partyBreakdown }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  const parties = useMemo(
    () => (partyBreakdown || [])
      .filter((p) => (Number(p.ayes) || 0) + (Number(p.noes) || 0) > 0)
      .map((p, i) => ({
        key: `p${i}`,
        name: p.party_name || 'Unknown',
        colour: p.party_colour || theme.palette.grey[500],
        ayes: Number(p.ayes) || 0,
        noes: Number(p.noes) || 0,
      })),
    [partyBreakdown, theme],
  );

  const rows = useMemo(() => {
    const aye = { name: 'Ayes', total: 0 };
    const no = { name: 'Noes', total: 0 };
    parties.forEach((p) => {
      aye[p.key] = p.ayes;
      no[p.key] = p.noes;
      aye.total += p.ayes;
      no.total += p.noes;
    });
    return [aye, no];
  }, [parties]);

  if (!parties.length) return null;

  // Recharts' dataKey is a lodash path, so party names (which can contain dots
  // and brackets) are mapped to p0/p1/... keys and the readable name is passed
  // via `name` for the legend and tooltip.
  const TotalLabel = (props) => {
    const { x, y, width, index } = props;
    const row = rows[index];
    if (!row || typeof x !== 'number') return null;
    return (
      <text
        x={x + width / 2}
        y={y - 8}
        textAnchor="middle"
        fill={t.ink}
        fontSize={14}
        fontWeight={600}
      >
        {row.total}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={rows} margin={{ top: 28, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
        <XAxis dataKey="name" tick={{ fontSize: 13, fill: theme.palette.text.secondary }}
               stroke={theme.palette.text.secondary} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
               stroke={theme.palette.text.secondary} />
        <RTooltip
          cursor={{ fill: theme.palette.divider }}
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            color: theme.palette.text.primary,
            fontSize: 12,
          }}
          itemStyle={{ color: theme.palette.text.primary }}
          labelStyle={{ color: theme.palette.text.primary }}
        />
        {/* Recharts colours each legend label with its series fill, which is
            illegible for dark party colours on a dark ground. The swatch keeps
            the party colour; the text stays ink. */}
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          formatter={(value) => (
            <span style={{ color: theme.palette.text.primary }}>{value}</span>
          )}
        />
        {parties.map((p, i) => (
          <Bar key={p.key} dataKey={p.key} name={p.name} stackId="a" fill={p.colour} maxBarSize={120}>
            {i === parties.length - 1 && <LabelList content={TotalLabel} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

/** One member card: photo left, name and party beneath — APH's grid cell. */
function MemberCard({ vote }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  const photo = vote.phid ? getParliamentarianPhotoUrl(vote.phid) : null;
  const name = vote.name || 'Unknown';
  const body = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {/* MUI Avatar falls back to its children when the photo 404s, which it
          does for many historical members — the grid never breaks. */}
      <Avatar
        src={photo || undefined}
        alt=""
        variant="rounded"
        sx={{
          width: 44, height: 44, flexShrink: 0,
          bgcolor: t.hover, color: t.faint, fontSize: '1rem',
          border: `${t.ruleWidth} solid ${t.ruleColor}`,
        }}
      >
        {initialsFor(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: vote.phid ? t.accentText : t.ink, lineHeight: 1.3 }}
        >
          {name}
        </Typography>
        {vote.party_name && (
          <Typography variant="body2" sx={{ color: t.muted, fontSize: '0.8125rem', lineHeight: 1.3 }}>
            {vote.party_name}
          </Typography>
        )}
      </Box>
    </Box>
  );
  return (
    <Box
      sx={{
        border: `${t.ruleWidth} solid ${t.ruleColor}`,
        borderRadius: 1.5,
        p: 1.25,
        height: '100%',
        '&:hover': vote.phid ? { bgcolor: t.hover } : undefined,
      }}
    >
      {vote.phid ? (
        <Link
          component={RouterLink}
          to={`/parliamentarians/${vote.phid}`}
          underline="none"
          sx={{ display: 'block' }}
        >
          {body}
        </Link>
      ) : body}
    </Box>
  );
}

function MemberGrid({ votes }) {
  return (
    <Grid container spacing={1.5}>
      {votes.map((v, i) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`${v.phid || v.name}-${i}`}>
          <MemberCard vote={v} />
        </Grid>
      ))}
    </Grid>
  );
}

/** Plain name list for divisions with no APH enrichment (no PHIDs, no photos). */
function NameList({ names }) {
  const theme = useTheme();
  const t = theme.palette.ledger;
  if (!names.length) {
    return <Typography variant="body2" sx={{ color: t.muted }}>No names recorded.</Typography>;
  }
  return (
    <Grid container spacing={0.5}>
      {names.map((n, i) => (
        <Grid item xs={12} sm={6} md={4} key={`${n}-${i}`}>
          <Typography variant="body2" sx={{ color: t.ink, py: 0.25 }}>{n}</Typography>
        </Grid>
      ))}
    </Grid>
  );
}

function downloadVotesCsv(division) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['name', 'phid', 'party', 'vote', 'paired'];
  const rows = division.votes
    ? division.votes.map((v) => [
        v.name, v.phid, v.party_name,
        v.vote === 'did_not_vote' ? 'did not vote' : v.vote,
        (v.pair || v.paired) ? 'yes' : '',
      ])
    : [
        ...division.spine.aye.map((n) => [n, '', '', 'aye', '']),
        ...division.spine.no.map((n) => [n, '', '', 'no', '']),
        ...division.spine.absent.map((n) => [n, '', '', 'absent', '']),
      ];
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `division-${division.id || 'results'}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ------------------------------------------------------------------- page

export default function DivisionDetail({ chamber = 'House' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const t = theme.palette.ledger;
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [grouping, setGrouping] = useState('party');

  const isSenate = chamber === 'Senate';
  const searchPath = isSenate ? '/senate-divisions' : '/divisions';
  const chamberLabel = isSenate ? 'Senate' : 'House of Representatives';
  const accent = chamberColor(chamberLabel);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      setTab(0);
      try {
        const data = isSenate ? await getSenateDivision(id) : await getDivision(id);
        if (active) setRaw(data);
      } catch (e) {
        if (active) setError('Division not found or failed to load.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, isSenate]);

  const d = useMemo(() => (raw ? normalise(raw, isSenate ? 'Senate' : 'House') : null), [raw, isSenate]);

  const groups = useMemo(() => {
    if (!d) return null;
    if (d.votes) {
      const aye = d.votes.filter((v) => v.vote === 'aye');
      const no = d.votes.filter((v) => v.vote === 'no');
      const other = d.votes.filter((v) => v.vote !== 'aye' && v.vote !== 'no');
      const allPaired = other.length > 0 && other.every((v) => v.pair || v.paired);
      return {
        enriched: true,
        tabs: [
          { label: 'Ayes', count: aye.length, items: aye },
          { label: 'Noes', count: no.length, items: no },
          ...(other.length
            ? [{ label: allPaired ? 'Pair' : 'Did not vote', count: other.length, items: other }]
            : []),
        ],
      };
    }
    return {
      enriched: false,
      tabs: [
        { label: 'Ayes', count: d.spine.aye.length, items: d.spine.aye },
        { label: 'Noes', count: d.spine.no.length, items: d.spine.no },
        ...(d.spine.absent.length
          ? [{ label: 'Absent', count: d.spine.absent.length, items: d.spine.absent }]
          : []),
      ],
    };
  }, [d]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !d) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">{error || 'Division not found.'}</Alert>
      </Box>
    );
  }

  const activeTab = groups.tabs[Math.min(tab, groups.tabs.length - 1)];
  const meta = [
    formatDate(d.date),
    chamberLabel,
    d.divisionNumber ? `Division ${d.divisionNumber}` : null,
  ].filter(Boolean).join(' \u00b7 ');

  // By-party sections: largest party first, as APH orders them.
  const partySections = (groups.enriched && grouping === 'party')
    ? (() => {
        const byParty = new Map();
        activeTab.items.forEach((v) => {
          const key = v.party_name || 'Unknown';
          if (!byParty.has(key)) {
            byParty.set(key, { name: key, colour: v.party_colour, items: [] });
          }
          byParty.get(key).items.push(v);
        });
        return Array.from(byParty.values()).sort((a, b) => b.items.length - a.items.length);
      })()
    : null;

  const sortedByName = (groups.enriched && grouping === 'member')
    ? [...activeTab.items].sort((a, b) => (
        surnameKey(a.name).localeCompare(surnameKey(b.name))
        || String(a.name || '').localeCompare(String(b.name || ''))
      ))
    : null;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 4 }}>
      <Button
        component={RouterLink}
        to={searchPath}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        {isSenate ? 'Senate divisions' : 'House divisions'}
      </Button>

      {/* ---- Header band: subject + facts on the left, tally box on the right */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <Chip
                label={chamberLabel}
                size="small"
                variant="outlined"
                sx={{ borderColor: accent, color: accent }}
              />
              <Chip
                label={d.outcome}
                size="small"
                color={d.passed ? 'success' : 'error'}
              />
              {d.conscienceVote && <Chip label="Conscience vote" size="small" variant="outlined" />}
              {d.castingVote && <Chip label="Casting vote" size="small" variant="outlined" />}
            </Box>

            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: t.ink, mb: 1, wordBreak: 'break-word' }}
            >
              {d.subject}
            </Typography>

            <Typography variant="body2" sx={{ color: t.muted, mb: 2 }}>
              {meta}
            </Typography>

            <Box>
              <FactRow label="Division number">
                {d.divisionNumber
                  ? `${d.divisionNumber}${d.aphDivisionNumber ? ` (${d.aphDivisionNumber} of this Parliament)` : ''}`
                  : null}
              </FactRow>
              <FactRow label="Mover">
                {d.mover && d.mover.name
                  ? (d.mover.phid
                      ? (
                        <Link component={RouterLink} to={`/parliamentarians/${d.mover.phid}`} underline="hover">
                          {d.mover.name}
                        </Link>
                      )
                      : d.mover.name)
                  : null}
              </FactRow>
              <FactRow label="Question">{d.question}</FactRow>
              <FactRow label="Context">{d.context}</FactRow>
              <FactRow label="Date">{formatDate(d.date)}</FactRow>
              <FactRow label="Outcome">{d.outcome}</FactRow>
              <FactRow label="Parliament">
                {d.parliament
                  ? (
                    <Link component={RouterLink} to={`/parliaments/${d.parliament}`} underline="hover">
                      {`Parliament ${d.parliament}${d.session ? `, session ${d.session}` : ''}`}
                    </Link>
                  )
                  : null}
              </FactRow>
              <FactRow label="Casting vote">{d.castingVoteReason}</FactRow>
              <FactRow label="Hansard record">{d.hansardLine}</FactRow>
            </Box>
          </Box>

          <Box sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, display: 'flex', justifyContent: 'center' }}>
            <TallyBox ayes={d.ayes} noes={d.noes} didNotVote={d.didNotVote} />
          </Box>
        </Box>

        {/* ---- Action row */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <ShareButton title={d.subject} />
          <CopyLinkButton size="small" />
          {(d.votesUrl || d.sourceUrl) && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={d.votesUrl || d.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Votes record on ParlInfo
            </Button>
          )}
          {d.bills.map((b) => {
            const label = isNarrow ? 'Bill' : (b.title || 'Bill');
            // Only render a link when the bill actually resolved to a document
            // — a dead /bills/<id> is worse than plain text.
            return b.id ? (
              <Button
                key={b.bill_id || b.title}
                size="small"
                variant="outlined"
                startIcon={<GavelIcon />}
                component={RouterLink}
                to={`/bills/${encodeURIComponent(b.id)}`}
              >
                {label}
              </Button>
            ) : (
              <Chip
                key={b.bill_id || b.title}
                icon={<GavelIcon />}
                label={label}
                size="small"
                variant="outlined"
              />
            );
          })}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadVotesCsv(d)}
          >
            Download results
          </Button>
        </Box>
      </Paper>

      {/* ---- Votes by party */}
      {d.partyBreakdown.length > 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            Votes by party
          </Typography>
          <VotesByPartyChart partyBreakdown={d.partyBreakdown} />
        </Paper>
      )}

      {/* ---- Individual votes */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Individual votes
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            gap: 1.5,
            borderBottom: `${t.ruleWidth} solid ${t.ruleColor}`,
            mb: 2,
          }}
        >
          <Tabs
            value={Math.min(tab, groups.tabs.length - 1)}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
          >
            {groups.tabs.map((tb) => (
              <Tab key={tb.label} label={`${tb.label} (${tb.count})`} />
            ))}
          </Tabs>

          {groups.enriched && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={grouping}
              onChange={(e, v) => { if (v) setGrouping(v); }}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, mb: { xs: 1, sm: 0.75 } }}
            >
              <ToggleButton value="party" sx={{ textTransform: 'none' }}>By party</ToggleButton>
              <ToggleButton value="member" sx={{ textTransform: 'none' }}>By member</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {!groups.enriched && (
          <Typography variant="body2" sx={{ color: t.muted, mb: 2 }}>
            This division predates the APH Divisions record, so the names below come
            from the Hansard division list only — there are no party groupings,
            member links or photographs for it.
          </Typography>
        )}

        {activeTab.count === 0 && (
          <Typography variant="body2" sx={{ color: t.muted }}>
            No members recorded in this group.
          </Typography>
        )}

        {groups.enriched && grouping === 'party' && partySections && partySections.map((sec) => (
          <Box key={sec.name} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Box
                sx={{
                  width: 10, height: 10, borderRadius: '2px', flexShrink: 0,
                  bgcolor: sec.colour || t.faint,
                }}
              />
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                {`${sec.name} (${sec.items.length})`}
              </Typography>
            </Box>
            <MemberGrid votes={sec.items} />
          </Box>
        ))}

        {groups.enriched && grouping === 'member' && sortedByName && (
          <MemberGrid votes={sortedByName} />
        )}

        {!groups.enriched && <NameList names={activeTab.items} />}
      </Paper>

      {/* ---- Provenance */}
      <Typography variant="body2" sx={{ color: t.faint, mt: 3 }}>
        {[
          'Source: ParlInfo Hansard division record',
          d.enriched ? 'enriched with the APH Divisions record (aph.gov.au)' : null,
        ].filter(Boolean).join(' \u00b7 ')}
      </Typography>
    </Box>
  );
}
